'use client';

import { FC, useContext, useState } from 'react';
import {
  Dialog,
  Flex,
  Button,
  ButtonGroup,
  Input,
  Textarea,
  Checkbox,
  InputChips,
} from '@/_components';
import { DropLinksContext } from '@/_contexts/DropLinksProvider';
import { DropLink } from '@/_types/models/dropLink';
import type { EditDropLinkDialogProps } from './EditDropLinkDialog.types';

export const EditDropLinkDialog: FC<EditDropLinkDialogProps> = ({
  isOpen,
  onClose,
}) => {
  const {
    selectedDropLink,
    setSelectedDropLink,
    firmUserOptions,
    updateDropLink,
    isLoading,
  } = useContext(DropLinksContext);

  const [chipToAddInputText, setChipToAddInputText] = useState('');

  const handleEditDropLinkChipsChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setChipToAddInputText(event.target.value);
  };

  const handleEditDropLinkSetChips = (
    chips: Set<string> | ((_prev: Set<string>) => Set<string>),
  ) => {
    const newChips =
      typeof chips === 'function'
        ? chips(
            new Set(
              selectedDropLink?.subscribed_user_ids.map((userId) =>
                userId.toString(),
              ) || [],
            ),
          )
        : chips;
    if (newChips.size === 0) {
      return;
    }
    setSelectedDropLink((prev: DropLink | null) =>
      prev
        ? {
            ...prev,
            subscribed_user_ids: Array.from(newChips).map(Number),
          }
        : null,
    );
  };

  const handleSubmit = async () => {
    if (!selectedDropLink) return;
    await updateDropLink(selectedDropLink);
    onClose();
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title="Edit Drop Link">
      <Dialog.Content>
        <Flex gap={10} isColumn={true}>
          <Input
            label="Name"
            placeholder="Enter drop link name (visible to clients)"
            value={selectedDropLink?.name || ''}
            onChange={(e) =>
              setSelectedDropLink((prev: DropLink | null) =>
                prev ? { ...prev, name: e.target.value } : null,
              )
            }
            isRequired={true}
          />
          <Textarea
            label="Custom Instruction"
            placeholder="Enter custom instruction (optional)"
            value={selectedDropLink?.custom_instruction || ''}
            onChange={(e) =>
              setSelectedDropLink((prev: DropLink | null) =>
                prev ? { ...prev, custom_instruction: e.target.value } : null,
              )
            }
            rows={4}
          />
          <Checkbox
            label="Hide Sign In Button"
            isChecked={selectedDropLink?.hide_sign_in_button || false}
            onChange={(e) =>
              setSelectedDropLink((prev: DropLink | null) =>
                prev
                  ? { ...prev, hide_sign_in_button: e.target.checked }
                  : null,
              )
            }
          />
          <InputChips
            label="Notify Users"
            placeholder="Select users to notify"
            chips={
              new Set(
                selectedDropLink?.subscribed_user_ids.map((userId) =>
                  userId.toString(),
                ) || [],
              )
            }
            setChips={handleEditDropLinkSetChips}
            onChange={handleEditDropLinkChipsChange}
            value={chipToAddInputText}
            results={firmUserOptions}
            chipNames={firmUserOptions}
          />
          <ButtonGroup align="right">
            <Button intent="secondary" value="Cancel" onClick={onClose} />
            <Button
              intent="primary"
              value="Save"
              onClick={handleSubmit}
              isDisabled={
                !selectedDropLink?.name ||
                !selectedDropLink?.subscribed_user_ids.length ||
                isLoading
              }
            />
          </ButtonGroup>
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
};
